import './App.css';
import '../node_modules/@identy/identy-face/dist/identy-face-style.css'
import { AppUI, Base64, FaceSDK, AsThreshold, Template, TransactionMode, PrecaptureChecks } from "@identy/identy-face";
import { Camera, CheckCircle, RotateCcw, XCircle, Shield, AlertTriangle } from "lucide-react";
import { Button } from "./components/ui/Button";
import { processFaceCapture } from './utils';
import { DialogComponent } from './components/ui/Dialog';
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import Loader from './components/ui/Loader';

// Constants
const APP_STATES = {
  INITIALIZING: 'initializing',
  READY: 'ready',
  CAPTURING: 'capturing',
  PROCESSING: 'processing',
  ERROR: 'error',
  SECURITY_ERROR: 'security_error'
};

const CAPTURE_STATES = {
  IDLE: 'idle',
  SUCCESS: 'success',
  FAILED: 'failed'
};

// Store original functions at page load to detect runtime modifications
const originalFunctions = {
  getUserMedia: navigator.mediaDevices.getUserMedia,
  RTCPeerConnection: window.RTCPeerConnection,
  getVideoTracks: window.MediaStream?.prototype?.getVideoTracks,
  getImageData: CanvasRenderingContext2D.prototype.getImageData,
  captureStream: HTMLVideoElement.prototype.captureStream
};

// Plugin Detection Functions
const detectMaliciousPlugins = () => {
  const suspiciousPlugins = [];

  // Check for modified getUserMedia function
  const currentGetUserMedia = navigator.mediaDevices.getUserMedia;
  if (currentGetUserMedia !== originalFunctions.getUserMedia ||
    currentGetUserMedia.toString().includes('[native code]') === false) {
    suspiciousPlugins.push({
      name: 'Modified Camera Access',
      type: 'injection',
      risk: 'critical',
      description: 'Camera access function has been modified by an extension'
    });
  }

  // Check for suspicious WebRTC modifications
  if (window.RTCPeerConnection &&
    (window.RTCPeerConnection !== originalFunctions.RTCPeerConnection ||
      window.RTCPeerConnection.toString().includes('[native code]') === false)) {
    suspiciousPlugins.push({
      name: 'Modified WebRTC',
      type: 'injection',
      risk: 'high',
      description: 'WebRTC functions have been modified'
    });
  }

  // Check for known malicious camera manipulation extensions by examining global objects
  const suspiciousGlobals = [
    'virtualCamera', 'faceSwap', 'backgroundRemover', 'cameraFilter',
    'videoModifier', 'streamProcessor', 'manyCam', 'snapCamera',
    'obsVirtualCam', 'chromaCam', 'xsplitVCam', 'nvidiaBroadcast'
  ];

  suspiciousGlobals.forEach(global => {
    if (window[global] || document.querySelector(`[id*="${global}"]`) ||
      document.querySelector(`[class*="${global}"]`)) {
      suspiciousPlugins.push({
        name: 'Camera Manipulation Software',
        type: 'extension',
        risk: 'critical',
        description: `Virtual camera or filter software detected: ${global}`
      });
    }
  });

  // Check for extension scripts in DOM (more comprehensive)
  const scripts = document.querySelectorAll('script');
  scripts.forEach(script => {
    const src = script.src?.toLowerCase() || '';
    const content = script.textContent?.toLowerCase() || '';

    const maliciousPatterns = [
      'camera-hijacker', 'fake-camera', 'video-spoof', 'cam-override',
      'deepfake', 'face-swap', 'virtual-cam', 'background-removal',
      'filter-cam', 'beauty-cam', 'snap-camera', 'obs-virtual'
    ];

    maliciousPatterns.forEach(pattern => {
      if (src.includes(pattern) || content.includes(pattern)) {
        suspiciousPlugins.push({
          name: 'Malicious Extension Script',
          type: 'extension',
          risk: 'critical',
          description: `Suspicious camera manipulation script detected: ${pattern}`
        });
      }
    });
  });

  // Check for suspicious MediaStream modifications
  if (window.MediaStream && window.MediaStream.prototype.getVideoTracks) {
    const currentGetVideoTracks = window.MediaStream.prototype.getVideoTracks;
    if (currentGetVideoTracks !== originalFunctions.getVideoTracks ||
      currentGetVideoTracks.toString().includes('[native code]') === false) {
      suspiciousPlugins.push({
        name: 'Modified MediaStream',
        type: 'injection',
        risk: 'high',
        description: 'MediaStream functions have been tampered with'
      });
    }
  }

  // Check for canvas manipulation modifications
  const currentGetImageData = CanvasRenderingContext2D.prototype.getImageData;
  if (currentGetImageData !== originalFunctions.getImageData ||
    currentGetImageData.toString().includes('[native code]') === false) {
    suspiciousPlugins.push({
      name: 'Modified Canvas API',
      type: 'injection',
      risk: 'medium',
      description: 'Canvas manipulation functions have been modified'
    });
  }

  // Check for video capture modifications
  try {
    const currentCaptureStream = HTMLVideoElement.prototype.captureStream;
    if (currentCaptureStream &&
      (currentCaptureStream !== originalFunctions.captureStream ||
        currentCaptureStream.toString().includes('[native code]') === false)) {
      suspiciousPlugins.push({
        name: 'Modified Video Capture',
        type: 'injection',
        risk: 'high',
        description: 'Video capture stream functions have been modified'
      });
    }
  } catch (e) {
    // Ignore if not supported
  }

  // Check for DOM mutations that might indicate extension injection
  const suspiciousElements = document.querySelectorAll('[id*="camera"], [id*="video"], [class*="filter"], [class*="virtual"]');
  if (suspiciousElements.length > 0) {
    let foundSuspicious = false;
    suspiciousElements.forEach(element => {
      const id = element.id?.toLowerCase() || '';
      const className = element.className?.toLowerCase() || '';
      if (id.includes('virtual') || id.includes('filter') ||
        className.includes('virtual') || className.includes('filter')) {
        foundSuspicious = true;
      }
    });

    if (foundSuspicious) {
      suspiciousPlugins.push({
        name: 'Suspicious DOM Elements',
        type: 'injection',
        risk: 'medium',
        description: 'DOM elements suggesting camera manipulation detected'
      });
    }
  }

  // Check for property descriptors modifications
  try {
    const getUserMediaDescriptor = Object.getOwnPropertyDescriptor(navigator.mediaDevices, 'getUserMedia');
    if (getUserMediaDescriptor && !getUserMediaDescriptor.value.toString().includes('[native code]')) {
      suspiciousPlugins.push({
        name: 'Property Descriptor Modification',
        type: 'injection',
        risk: 'critical',
        description: 'Camera API property descriptors have been modified'
      });
    }
  } catch (e) {
    // Ignore if check fails
  }

  return suspiciousPlugins;
};

const checkCameraSecurityStatus = async () => {
  const securityIssues = [];

  try {
    // Check camera permission status
    const permission = await navigator.permissions.query({ name: 'camera' });

    // Check if camera is already in use
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1, height: 1 },
        audio: false
      });

      // Clean up immediately
      stream.getTracks().forEach(track => track.stop());
    } catch (error) {
      if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        securityIssues.push({
          name: 'Camera In Use',
          type: 'warning',
          risk: 'medium',
          description: 'Camera appears to be in use by another application or extension'
        });
      }
    }

    // Detect plugins
    const plugins = detectMaliciousPlugins();
    securityIssues.push(...plugins);

  } catch (error) {
    console.warn('Security check failed:', error);
  }

  return securityIssues;
};

function App() {
  // Configuration
  const config = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return {
      urlBase: process.env.REACT_APP_IDENTY_FACE_URL,
      modelsUrlBase: process.env.REACT_APP_IDENTY_MODELS_URL || process.env.REACT_APP_IDENTY_FACE_URL,
      faceConfig: searchParams.get("key"),
      assisted: searchParams.get("assisted") === "true",
    };
  }, []);

  // App state
  const [appState, setAppState] = useState(APP_STATES.INITIALIZING);
  const [initError, setInitError] = useState('');
  const [securityIssues, setSecurityIssues] = useState([]);
  const securityIntervalRef = useRef(null);

  // Capture state
  const [captureState, setCaptureState] = useState(CAPTURE_STATES.IDLE);
  const [capturedImage, setCapturedImage] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Security check function for initial check
  const performInitialSecurityCheck = useCallback(async () => {
    try {
      const issues = await checkCameraSecurityStatus();
      const criticalIssues = issues.filter(issue =>
        issue.risk === 'critical' || issue.risk === 'high'
      );

      setSecurityIssues(issues);

      if (criticalIssues.length > 0) {
        setAppState(APP_STATES.SECURITY_ERROR);
        return false;
      }


      return true;
    } catch (error) {
      console.error('Initial security check failed:', error);
      return true;
    }
  }, []);

  // Security check function for interval checks - reloads page if malicious found
  const performIntervalSecurityCheck = useCallback(async () => {
    try {
      const issues = await checkCameraSecurityStatus();
      const criticalIssues = issues.filter(issue =>
        issue.risk === 'critical' || issue.risk === 'high'
      );

      if (criticalIssues.length > 0) {
        // Clear interval before reload
        if (securityIntervalRef.current) {
          clearInterval(securityIntervalRef.current);
          securityIntervalRef.current = null;
        }
        // Reload the page immediately
        window.location.reload();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Interval security check failed:', error);
      return true;
    }
  }, []);

  // Start security monitoring with 5-second intervals
  const startSecurityMonitoring = useCallback(() => {

    // Clear any existing interval
    if (securityIntervalRef.current) {
      clearInterval(securityIntervalRef.current);
      securityIntervalRef.current = null;
    }

    // Set up interval - THIS IS THE SETINTERVAL
    securityIntervalRef.current = setInterval(() => {
      performIntervalSecurityCheck();
    }, 5000);

  }, [performIntervalSecurityCheck]);

  // Stop security monitoring
  const stopSecurityMonitoring = useCallback(() => {
    if (securityIntervalRef.current) {
      clearInterval(securityIntervalRef.current);
      securityIntervalRef.current = null;
    }
  }, []);

  // Initialize FaceSDK
  const initializeSDK = useCallback(async () => {
    try {
      // Perform initial security check
      const isSecure = await performInitialSecurityCheck();
      if (!isSecure) {
        return;
      }

      const modelURL = `${config.modelsUrlBase}/api/v1/models`;
      const pubKeyURL = `${config.urlBase}/api/v1/pub_key`;
      const timeURL = `${config.urlBase}/api/v1/time`;

      await FaceSDK.preInitialize(
        { URL: modelURL },
        { URL: pubKeyURL },
        { URL: timeURL }
      );

      setAppState(APP_STATES.READY);

      // Start continuous security monitoring after successful initialization
      startSecurityMonitoring();
    } catch (error) {
      if (process.env.REACT_APP_ENV !== 'production') {
        console.error('SDK initialization failed:', error);
      }
      setInitError('Failed to initialize FaceSDK. Please try again later.');
      setAppState(APP_STATES.ERROR);
    }
  }, [config.urlBase, config.modelsUrlBase, performInitialSecurityCheck, startSecurityMonitoring]);

  // Process captured face data
  const processCapture = useCallback(async (sdk, capResult) => {
    try {
      const jsonString = atob(config.faceConfig);
      const json = JSON.parse(jsonString);
      const response = await processFaceCapture(capResult, json.id, sdk.username);

      console.log(response.data)

      if (response.data.code !== 200) {
        throw new Error(response.data.message || 'Face capture failed');
      }

      const quality = response.data.data.quality;
      const icao = response.data.icao_data;

      if (quality.eyes_open_metric < json.fv ?? 15) {
        throw new Error("Face elements (eyes) not fully visible.");
      } else if (icao.background_continuity < json.bc ?? 50) {
        throw new Error("Background not clear.");
      } else if (icao.multiple_faces) {
        throw new Error("Multiple faces detect.");
      } else if (icao.eyeR_visibility < (json.fv ?? 20) || icao.eyeL_visibility < (json.fv ?? 20) || icao.mouth_visibility < (json.fv ?? 20)) {
        throw new Error("Face elements not fully visible.");
      } else if (icao.illumination_uniformity < json.iu ?? 50) {
        throw new Error("Face lighting is uneven.");
      }

      // Set captured image and success state
      setCapturedImage(response.data.data.templates.JPEG || null);
      setCaptureState(CAPTURE_STATES.SUCCESS);

      setTimeout(() => {
        setCapturedImage(null);
        setErrorMessage("Face capture timed out. Please try again.");
        setCaptureState(CAPTURE_STATES.FAILED);
      }, 60000);

      return {
        transactionId: response.data.id,
        code: response.data.code,
        feedbackCode: response.data.message,
        quality: response.data.quality ? {
          eyeVisibility: response.data.quality.eyes_visibility,
          eyesStatus: response.data.quality.eyes_status,
          eyesOpenMetric: response.data.quality.eyes_open_metric,
          qualityMetric: response.data.quality.quality_metric,
          faceOcclusionsCheck: response.data.quality.face_occlusions_check,
          faceOcclusionsCheckScore: response.data.quality.face_occlusions_score
        } : null,
        debug: response.data.debug,
        icao: response.data.icao_data
      };
    } catch (error) {
      if (process.env.REACT_APP_ENV !== 'production') {
        console.error('Capture processing failed:', error);
      }
      const message = error?.response?.data?.message ||
        error?.message ||
        'Face capture failed';
      setErrorMessage(message);
      setCaptureState(CAPTURE_STATES.FAILED);
      throw error;
    }
  }, [config.faceConfig]);

  // Handle face capture
  const handleCapture = useCallback(async () => {
    if (appState !== APP_STATES.READY) return;

    // Don't need to check security here since it's continuously monitored
    setAppState(APP_STATES.CAPTURING);

    try {
      const options = {
        enableAS: true,
        requiredTemplates: [Template.JPEG],
        showCaptureTraining: false,
        base64EncodingFlag: Base64.NO_WRAP,
        allowClose: true,
        enableEyesStatusDetector: true,
        skipSupportCheck: false,
        backend: "wasm",
        transaction: { type: TransactionMode.CAPTURE },
        appUI: AppUI.STANDARD,
        allowCameraSelect: false,
        asThreshold: AsThreshold.BALANCED_VERY_HIGH,
        captureAttempts: 5,
        captureTimeout: 50000,
        enableICAOChecks: true,
        enableGImage: true,
        precaptureCheck: true,
        precaptureOptions: new Map([
        [PrecaptureChecks.GLASSES, true],
        [PrecaptureChecks.HAND, true],
        [PrecaptureChecks.HAT, true],
        [PrecaptureChecks.MASK, true],
        [PrecaptureChecks.SUNGLASSES, true],
        [PrecaptureChecks.EYE_OPENNESS, true],
        [PrecaptureChecks.MOUTH_CLOSURE, true]
      ]),
        assisted: config.assisted,
        events: {},
        localization: {
          en: {
            FEEDBACK_RETRY:"Something is weird, move slightly to change lighting",
            FEEDBACK_RETRY_INSECURE:"Insecure camera feed",
            FEEDBACK_RETRY_QUALITY:"Bad Quality, Retry Capture",
            FEEDBACK_ENROLLED:"Registered Successfully",
            FEEDBACK_CAPTURED:"Captured Successfully",
            FEEDBACK_NOT_ENROLLED:"User Not Enrolled",
            FEEDBACK_VERIFY_COMPLETED:"Verification Successful",
            FEEDBACK_VERIFY_FAILED:"Verification failed",
            FEEDBACK_SEARCHING:"Searching...",
            FEEDBACK_INSIDE_GUIDE:"Please be inside the guide",
            FEEDBACK_PLEASE_HOLD:"Please hold.",
            FEEDBACK_STABLE:"Please be stable",
            FEEDBACK_EYE_LEVEL:"Look Straight",
            FEEDBACK_UP_FACING:"Look Down to be parallel to camera",
            FEEDBACK_DOWN_FACING:"Look up to be parallel to camera",
            FEEDBACK_FACE_OUTSIDE:"Please be inside guide",
            FEEDBACK_CLOSE:"Move Away",
            FEEDBACK_FAR:"Move Close",
            FEEDBACK_LEFT_FACING:"Looking Left, Please Look straight",
            FEEDBACK_RIGHT_FACING:"Looking Right, Please Look straight",
            FEEDBACK_ROTATED:"Look Straight",
            FEEDBACK_NOT_STABLE:"Please be stable",
            FEEDBACK_OK:"Please Hold",
            FEEDBACK_NO:"Checking",
            FEEDBACK_NOT_CENTERED:"Center Face in oval",
            FEEDBACK_CAMERA_ACQUIRING_FAILED:"Cannot acquire camera, Allow permission Or Retry Capture",
            FEEDBACK_PROCESSING:"Processing...",
            FEEDBACK_UPLOAD_FAILURE:"Internal error retry",
            FEEDBACK_INITIALIZATION:"Initializing...",
            FEEDBACK_CHANGE_LIGHT:"Avoid shadows",
            FEEDBACK_CHANGE_LIGHT_TOO_BRIGHT: 'Too Bright, Move to better lighting.',
            FEEDBACK_CHANGE_LIGHT_TOO_DARK: 'Too Dark, Move to better lighting.',
            FEEDBACK_BUTTON_CLOSE:"Close",
            FEEDBACK_BUTTON_RETRY:"Retry",
            FEEDBACK_TRAINING_BUTTON_NEXT:"Next",
            FEEDBACK_TRAINING_LABEL:"Dont Show again",
            FEEDBACK_ORIENTATION_NOT_SUPPORTED:"Only Portrait mode is supported",
            FEEDBACK_TRAINING_DIALOG_CLOSED:"Training dialog, exited",
            FEEDBACK_LICENCE_INVALID:"License Invalid!!",
            FEEDBACK_OPEN_EYES : "Open your eyes",
            FEEDBACK_CLOSE_MOUTH : "Close your mouth",
            FEEDBACK_REMOVE_GLASSES : "Remove your glasses",
            FEEDBACK_REMOVE_HAND : "Remove object from your face",
            FEEDBACK_REMOVE_HAT : "Remove your hat",
            FEEDBACK_REMOVE_MASK : "Remove mask",
            FEEDBACK_REMOVE_SUNGLASSES : "Remove sunglasses",
            ERROR_BROWSER_NOT_SUPPORTED:"Browser not supported. Please use Chrome, Firefox, Opera, Edge(iOS, Android, Desktop), Samsung Internet (Android) or Safari (iOS, MAC)",
            ERROR_BROWSER_VERSION_NOT_SUPPORTED:"Browser version not supported. Please update to latest version available.",
            ERROR_DEVICE_NOT_SUPPORTED:"Device not supported, Please use mobile device or desktop.",
            ERROR_DEVICE_NOT_SUPPORTED_ANDROID:"Android version not supported. Please update to latest version available",
            ERROR_DEVICE_NOT_SUPPORTED_IOS:"IOS version not supported. Please update to latest version available",
            ERROR_DEVICE_NOT_SUPPORTED_MEMORY:"Device not supported. Insufficient Memory",
            ERROR_PLATFORM_NOT_SUPPORTED_ANDROID:"Android version not supported. Please update to latest version available",
            ERROR_PLATFORM_NOT_SUPPORTED_IOS:"IOS version not supported for this browser. Please update to latest version available",
            ERROR_BROWSER_FEATURE_NOT_SUPPORTED:"TextDecoder or Crypto API not present.",
            ERROR_WEBRTC_NOT_SUPPORTED:"Webrtc not supported",
            ERROR_MODEL_FAIL:"Model detection failed",
            ERROR_SERVER_INTERNAL:"Internal Server Error",
            ERROR_SERVER_CONNECTION_FAILURE:"Server connection failure"
        }
        }
      };

      const faceSDK = new FaceSDK(options);
      let photo;
      try {
        photo = await faceSDK.capture();
      } catch (captureError) {
        // capture() rejects when captureAttempts/captureTimeout is exhausted or
        // the SDK aborts — surface it instead of silently returning to READY.
        setErrorMessage('Unable to complete capture after several attempts. Please try again.');
        setCaptureState(CAPTURE_STATES.FAILED);
        throw captureError;
      }

      // Switch to processing state
      setAppState(APP_STATES.PROCESSING);
      await processCapture(faceSDK, photo);
    } catch (error) {
      // Error handling is done in processCapture
    } finally {
      setAppState(APP_STATES.READY);
    }
  }, [appState, config, processCapture]);

  // Handle confirm capture
  const handleConfirm = useCallback(() => {
    window.parent.postMessage({
      status: 200,
      message: "Capture successful!",
    }, "*");
    setCaptureState(CAPTURE_STATES.IDLE);
  }, []);

  // Handle retry capture
  const handleRetry = useCallback(() => {
    setCaptureState(CAPTURE_STATES.IDLE);
    setErrorMessage('');
    handleCapture();
  }, [handleCapture]);

  // Reset capture state
  const resetCaptureState = useCallback(() => {
    setCaptureState(CAPTURE_STATES.IDLE);
    setCapturedImage(null);
    setErrorMessage('');
  }, []);

  // Handle recapture
  const handleRecapture = useCallback(() => {
    resetCaptureState();
    handleCapture();
  }, [resetCaptureState, handleCapture]);

  // Handle security retry
  const handleSecurityRetry = useCallback(() => {
    setSecurityIssues([]);
    // Stop any existing monitoring before restarting
    stopSecurityMonitoring();
    // Restart security monitoring and SDK initialization
    initializeSDK();
  }, [initializeSDK, stopSecurityMonitoring]);

  // Initialize SDK on mount and cleanup on unmount
  useEffect(() => {
    initializeSDK();

    // Cleanup function to stop monitoring when component unmounts
    return () => {
      if (securityIntervalRef.current) {
        clearInterval(securityIntervalRef.current);
        securityIntervalRef.current = null;
      }
    };
  }, [initializeSDK]);

  // Render loading state
  if (appState === APP_STATES.INITIALIZING || appState === APP_STATES.PROCESSING) {
    const loadingMessage = appState === APP_STATES.INITIALIZING
      ? 'Initializing Face Capture...'
      : 'Processing your image...';

    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-white">
        <Loader />
        <p className="text-gray-600 mt-4">{loadingMessage}</p>
      </div>
    );
  }

  // Render security error state
  if (appState === APP_STATES.SECURITY_ERROR) {
    const criticalIssues = securityIssues.filter(issue =>
      issue.risk === 'critical' || issue.risk === 'high'
    );

    return (
      <div className="h-screen w-screen flex items-center justify-center bg-white p-6">
        <div className="max-w-md text-center space-y-6">
          <div className="flex justify-center">
            <Shield className="w-16 h-16 text-red-600" />
          </div>

          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-red-600">Security Warning</h2>
            <p className="text-gray-700 font-medium">
              A malicious add-on was detected that could interfere with face capture.
            </p>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left">
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-red-800">
                <strong>Detected Issues:</strong>
              </div>
            </div>
            <ul className="space-y-2">
              {criticalIssues.map((issue, index) => (
                <li key={index} className="text-sm text-red-700">
                  <strong>{issue.name}:</strong> {issue.description}
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Please disable any camera-related browser extensions or add-ons and try again.
            </p>

            <div className="space-y-2">
              {/*
              <Button
                onClick={handleSecurityRetry}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Retry After Disabling Add-ons
              </Button>
              */}

              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="w-full"
              >
                Refresh Page
              </Button>
            </div>
          </div>

          <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
            <strong>Tip:</strong> Look for extensions like virtual cameras, face filters,
            or video manipulation tools in your browser's extension menu and temporarily disable them.
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (appState === APP_STATES.ERROR) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-white">
        <div className="text-center space-y-4">
          <h2 className="text-xl font-semibold text-red-600">Initialization Failed</h2>
          <p className="text-gray-600">{initError}</p>
          <Button onClick={() => window.location.reload()} variant="outline">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const isCapturing = appState === APP_STATES.CAPTURING;
  const isDisabled = isCapturing || appState === APP_STATES.PROCESSING;

  return (
    <div className="min-h-screen max-h-screen flex flex-col bg-white">
      <div className="max-w-6xl mx-auto px-6 flex-1 py-16">
        <div className="flex flex-col lg:flex-row gap-12 items-start">

          {/* Guide Section - hidden on small screens */}
          <div className="flex-1 space-y-8 hidden lg:block">
            <div className="space-y-6 mb-12">
              <h1 className="text-3xl font-bold">Face Capture Guide</h1>
              <p className="text-lg text-muted-foreground">Follow these steps for the best results</p>
            </div>

            <div className="space-y-1">
              <h2 className="text-xl font-semibold">1. Position Yourself</h2>
              <p className="text-muted-foreground">
                Sit 18-24 inches from your camera with good lighting on your face.
              </p>
            </div>

            <div className="space-y-1">
              <h2 className="text-xl font-semibold">2. Camera Setup</h2>
              <p className="text-muted-foreground">Make sure your camera is at eye level and the lens is clean.</p>
            </div>

            <div className="space-y-1">
              <h2 className="text-xl font-semibold">3. Environment Check</h2>
              <p className="text-muted-foreground">Choose a well-lit area with minimal background distractions.</p>
            </div>

            <div className="space-y-1">
              <h2 className="text-xl font-semibold">4. Capture Process</h2>
              <p className="text-muted-foreground">Look directly at the camera and remain still during capture.</p>
            </div>

            {/* Security status indicator */}
            {securityIssues.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <strong>Security Notice:</strong> {securityIssues.length} potential issue(s) detected but not blocking capture.
                  </div>
                </div>
              </div>
            )}

            <div className="pt-8">
              <Button disabled={isDisabled} onClick={handleCapture} size="sm">
                <Camera className="h-5 w-5 mr-2" />
                {isCapturing ? 'Capturing...' : isDisabled ? 'Processing...' : 'Start Face Capture'}
              </Button>
            </div>
          </div>

          {/* Image + Button Section */}
          <div className="flex-1 lg:max-w-md flex flex-col items-center min-h-0 max-h-screen overflow-hidden">
            <img
              src="/face-capture-demo.png"
              alt="Proper face capture positioning demonstration"
              className="max-h-[70vh] h-auto rounded-lg shadow-lg object-contain"
            />

            {/* Show button only on small screens */}
            <div className="pt-6 w-full lg:hidden">
              <Button disabled={isDisabled} onClick={handleCapture} size="sm" className="w-full">
                <Camera className="h-5 w-5 mr-2" />
                {isCapturing ? 'Capturing...' : isDisabled ? 'Processing...' : 'Start Face Capture'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Success Dialog */}
      <DialogComponent
        open={captureState === CAPTURE_STATES.SUCCESS}
        onOpenChange={resetCaptureState}
        title="Face Captured Successfully"
        image={capturedImage}
        description={
          <p className="text-center text-sm text-muted-foreground mt-2">
            Your face has been successfully captured and verified.
          </p>
        }
        actions={
          <div className="flex gap-3">
            <Button onClick={handleRecapture} variant="outline" className="flex-1 bg-black">
              <RotateCcw className="w-4 h-4 mr-2" />
              Recapture
            </Button>
            <Button onClick={handleConfirm} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
              <CheckCircle className="w-4 h-4 mr-2" />
              Confirm
            </Button>
          </div>
        }
      />

      {/* Failed Dialog */}
      <DialogComponent
        open={captureState === CAPTURE_STATES.FAILED}
        onOpenChange={resetCaptureState}
        title="Face Capture Failed"
        icon={<XCircle className="w-6 h-6 text-red-600" />}
        description={
          <p className="text-center text-sm text-muted-foreground mt-2">
            {errorMessage || "Unable to detect a clear face. Please ensure good lighting and face the camera directly."}
          </p>
        }
        actions={
          <Button onClick={handleRetry} className="w-full">
            Retry
          </Button>
        }
      />
    </div>
  );
}

export default App;
